import debounceFn from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isBrowser, loadGoogleMapScript } from './utils';

interface UsePlacesAutocompleteServiceProps {
  apiKey?: string;
  debounce?: number;
  options?: Partial<google.maps.places.AutocompleteRequest>;
  sessionToken?: boolean;
}

export default function useGooglePlaces({
  apiKey,
  debounce = 300,
  options = {},
  sessionToken,
}: UsePlacesAutocompleteServiceProps) {
  const googlePlacesApiScriptUrl: string = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places`;

  const [placePredictions, setPlacePredictions] = useState<
    (google.maps.places.PlacePrediction | null)[]
  >([]);
  const [isPlacePredsLoading, setIsPlacePredsLoading] =
    useState<boolean>(false);
  const [placeInputValue, setPlaceInputValue] = useState<string | null>(null);
  const autocompleteSession =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const handleLoadScript = useCallback(
    () =>
      loadGoogleMapScript(googlePlacesApiScriptUrl, googlePlacesApiScriptUrl),
    [googlePlacesApiScriptUrl]
  );

  const debouncedPlacePredictions = useCallback(
    debounceFn((optionsArg: google.maps.places.AutocompleteRequest) => {
      if (optionsArg.input)
        google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          ...(sessionToken && autocompleteSession.current
            ? { sessionToken: autocompleteSession.current }
            : {}),
          ...options,
          ...optionsArg,
        }).then(({ suggestions }) =>
          setPlacePredictions(
            suggestions.map((suggestion) => suggestion.placePrediction)
          )
        );
    }, debounce),
    [debounce]
  );

  useEffect(() => {
    if (!isBrowser) return;

    const buildService = () => {
      if (typeof google === 'undefined' || !google)
        return console.error(
          'Google has not been found. Make sure your provide apiKey prop.'
        );

      if (sessionToken)
        autocompleteSession.current =
          new google.maps.places.AutocompleteSessionToken();
    };

    if (apiKey) {
      handleLoadScript().then(() => buildService());
    } else {
      buildService();
    }
  }, []);

  return {
    autocompleteSessionToken: autocompleteSession.current,
    placePredictions: placeInputValue ? placePredictions : [],
    isPlacePredictionsLoading: isPlacePredsLoading,
    getPlacePredictions: (request: google.maps.places.AutocompleteRequest) => {
      if (request.input) {
        setPlaceInputValue(request.input);
        setIsPlacePredsLoading(true);
        debouncedPlacePredictions(request);
        return;
      }
      setPlacePredictions([]);
      setPlaceInputValue(null);
      debouncedPlacePredictions(request);
      setIsPlacePredsLoading(false);
    },
    refreshSessionToken: () => {
      autocompleteSession.current =
        new google.maps.places.AutocompleteSessionToken();
    },
  };
}
